/**
 * SectionLabel — small uppercase header used INSIDE a card to mark a
 * sub-section (e.g. "Buddy Groups", "My Events", "Partner", "Payment").
 *
 * Visual model: a coloured icon + tracked uppercase label, followed by
 * a thin underline rule that fills the rest of the row. The colour
 * tokens are intentionally limited (primary, violet, amber, muted) so
 * sub-sections across the app keep a consistent, predictable palette.
 *
 * Pair this with a coloured left accent stripe on the cards inside the
 * section for the strongest read; the dashboard "Upcoming Rodeos"
 * card is the reference implementation.
 */

import type { ReactNode } from 'react';

export type SectionLabelColor = 'primary' | 'violet' | 'amber' | 'muted';

interface SectionLabelProps {
  /** Optional leading icon (e.g. <Users className="h-3 w-3" />). */
  icon?: ReactNode;
  children: ReactNode;
  /** Colour token for the icon + label text. Defaults to primary. */
  color?: SectionLabelColor;
  /** Optional trailing element (e.g. a count badge). */
  trailing?: ReactNode;
  className?: string;
}

const COLOR_CLASS: Record<SectionLabelColor, string> = {
  primary: 'text-primary',
  violet: 'text-violet-700 dark:text-violet-300',
  amber: 'text-amber-700 dark:text-amber-300',
  muted: 'text-muted-foreground',
};

export function SectionLabel({
  icon,
  children,
  color = 'primary',
  trailing,
  className,
}: SectionLabelProps) {
  const colorClass = COLOR_CLASS[color];
  return (
    <div className={'flex items-center gap-1.5 ' + (className ?? '')}>
      <div className={'flex items-center gap-1.5 ' + colorClass}>
        {icon}
        <span className="text-[11px] font-bold uppercase tracking-wider">
          {children}
        </span>
      </div>
      <div className="flex-1 h-px bg-border" />
      {trailing}
    </div>
  );
}
